import ReactDOM from 'react-dom';
import { MetricMetadataModalProps } from '../services/types';
import LoadingIndicator from './LoadingIndicator';
// import RaceEthnicityMetadataChart from './charts/RaceEthnicityMetadataChart';
import { capitalizeFirstLetter, formatValue } from '../utils/helpers';
import { useIframeContext } from '../context/IframeContext';
import { useEffect } from 'react';
import DistributionChart from './charts/DistributionChart';
import metadataIconWhite from '../assets/metric-metadata-icon-white.svg';
import { skipHistogram } from '../utils/chartUtils';

const MetricMetadataModal: React.FC<MetricMetadataModalProps> = ({ isOpen, onClose, id, metadata, distributionData, countyDistributionData, loading, error }) => {
    const { isIframe } = useIframeContext();

    // Hide main scrollbar when the metadata panel is open
    useEffect(() => {
        if (isOpen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }

        return () => {
          document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const metricName = metadata ? metadata[0].metric_name : '';
    const lod = metadata ? metadata[0].versions[0].level_of_detail : '';

    // Define selected geography average distribution value and unit
    const distribution = distributionData ? distributionData[0] : null;
    const dataEntries = distribution?.versions[0].data || [];

    // Calculate geoAverage: Sum all values and divide by the count
    const geoAverage =
    dataEntries.length > 0
        ? dataEntries.reduce((sum, entry) => sum + entry.value, 0) / dataEntries.length
        : 0;

    const unit = distribution?.versions[0].unit || 'integer';
    const unitDescriptor = distribution?.versions[0].unit_descriptor || '';  

    // Define county average distribution value and unit
    const countyDistribution = countyDistributionData ? countyDistributionData[0] : null;
    const countyAverage = countyDistribution?.versions[0].avg_value || 0;
    const cUnit = countyDistribution?.versions[0].unit || 'integer';

    // Determine if metric needs a generated histogram chart
    const renderHistogram = !skipHistogram.includes(id);

    return ReactDOM.createPortal(
        <div className={`fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-35 transition-all duration-500 transform ${!isOpen ? 'translate-y-full' : 'translate-y-0'} ${!isIframe && 'max-w-[456px] mx-auto'}`}>
            <div className={`bg-white mx-4 w-full shadow-xl overflow-y-auto h-4/5 relative rounded-t-md  transition-transform duration-500 transform ${!isOpen ? 'translate-y-full' : 'translate-y-0'}`}>
                <div className="sticky top-0 z-10 flex items-center justify-between bg-lacey-green px-6 py-3">
                    <div className="flex items-center">
                        <img className="mr-2" src={metadataIconWhite} alt="Metric Icon" />
                        <span className="text-white text-sm font-bold">{metricName}</span>
                    </div>
                    <button
                        className="text-white text-3xl hover:text-lightGray"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        &times;
                    </button>
                </div>
                <div className='p-6'>
                {loading ? (
                    <LoadingIndicator />
                ) : error ? (
                    <p className="text-red-500 text-sm">Error fetching metric metadata.</p>
                ) : metadata && metadata.length > 0 ? (
                    <div>
                        {metadata.map((metric) => { 
                            return (
                            <div key={metric.id} className="mb-4">
                                {/* <img src={svg} className='h-10 w-10' alt={`${metric.metric_name} icon`} /> */}
                                <h3 className="text-lacey-text text-xs font-bold">{metric.metric_name}</h3>
                                <p className='text-lacey-text text-xs w-11/12'>{metric.definition}</p>
                                {metric.versions && metric.versions.length > 0 && (
                                    <div>
                                        {metric.versions.map((version) => (
                                            <div key={version.metric_version_id_ext} className="my-2">
                                                <p className='text-lacey-text font-bold text-xs mt-3'>Why Include This Metric?</p>
                                                <p className='text-lacey-text text-xs w-11/12'>{version.why_important}</p>
                                                <div className='flex w-full mt-6'>
                                                    <div className='flex flex-col w-full'>
                                                        <p className='text-lacey-text font-bold text-xs'>Data Year</p>
                                                        <p className='text-lacey-text text-xs'> {version.year}</p>
                                                    </div>
                                                    <div className='flex flex-col w-full'>
                                                        <p className='text-lacey-text font-bold text-xs'>Scope</p>
                                                        <p className='text-lacey-text text-xs'> {capitalizeFirstLetter(version.level_of_detail)}</p>
                                                    </div>
                                                </div>
                                                <p className='text-lacey-text font-bold text-xs mt-3'>Source</p>
                                                <p className='text-lacey-text text-xs w-11/12'>{version.source}</p>
                                                {version.source_url && (
                                                    <div className='text-[#337AB7] text-xs mt-3 w-11/12 space-y-1'>
                                                        {version.source_url
                                                        .split(',')
                                                        .map((url, index) => {
                                                            const trimmedUrl = url.trim();
                                                            return (
                                                            <p key={index}>
                                                                <a
                                                                className='break-words overflow-hidden text-ellipsis'
                                                                href={trimmedUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                >
                                                                {trimmedUrl}
                                                                </a>
                                                            </p>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Indicator Histogram - conditionally rendered based on exclusions */}
                                { renderHistogram && <>
                                <div className='bg-[#F0F0F0]'>
                                    <p className='text-lacey-text text-center font-bold text-xs mt-4 py-2 border-b-[1px]'>{metricName} Histogram</p>

                                    <DistributionChart distributionData={distributionData} countyDistributionData={countyDistributionData} id={id} lod={lod}/>

                                    <p className='text-lacey-text text-[11px] text-center'>{unitDescriptor}</p>

                                    <div className='flex justify-around items-end'>
                                        <p className='text-lacey-text text-[11px] my-3'>Selected Area: <strong>{formatValue(geoAverage, unit, id)}</strong></p>

                                        <p className='text-lacey-text text-[11px] my-3'>Thurston County: <strong>{formatValue(countyAverage, cUnit, id)}</strong></p>    
                                    </div>
                                </div>
                                <p className='text-lacey-text text-xs font-bold w-11/12 mt-4'>How to read this graph:</p>
                                <p className='text-lacey-text text-xs w-11/12 mt-2'>{`The histogram shows the share of ${lod}s from both the selected area and the total geography that fall within each quintile of data. Quintiles are determined by taking all of the data for each metric, and dividing the data into 5 equal categories. The histogram has two main components - the selected ${lod}s and all ${lod}s in the total geography we are comparing to.`}</p>
                                <p className='text-lacey-text text-xs w-11/12 mt-2'>{`The percentages on the histogram bars refer to the share of ${lod}s from each geography (selected and total) that fall into each of the 5 quintiles. This helps to understand the distribution of the data for the selected area compared to the total geography. The selected area average and total geography average are displayed beneath the chart for a more high level comparison.`}</p>
                                </>
                                }
                            </div>
                            )
                        })}
                    </div>
                ) : (
                    <p>No metadata available.</p>
                )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default MetricMetadataModal;